// Reusable inline text-style editor used across the JD Creation module.
//
// - Opens as a popover anchored to a "Format" trigger button.
// - Every control writes into a controlled `style` object of shape
//   { fontFamily, fontSize, fontWeight, bold, italic, underline, textAlign,
//     lineHeight }.
// - An empty value on any control means "inherit from parent" and is stripped
//   from the persisted JSON by `pruneStyle`.

import { useState } from "react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
    Bold,
    Italic,
    Underline,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Type,
    RotateCcw,
} from "lucide-react";
import {
    FONT_FAMILIES,
    FONT_SIZES,
    FONT_WEIGHTS,
    LINE_HEIGHTS,
    TEXT_ALIGNMENTS,
    EMPTY_STYLE,
} from "@/lib/jdStyles";

// ----------------------------------------------------------------------------
// Toggle button — small square toggle for bold/italic/underline/align.
// ----------------------------------------------------------------------------
function Toggle({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            aria-pressed={active}
            aria-label={label}
            title={label}
            className={`w-9 h-9 inline-flex items-center justify-center border transition-colors ${
                active
                    ? "bg-[#0F1F38] text-[#F7F5F0] border-[#0F1F38]"
                    : "bg-white text-[#0F1F38] border-[#0F1F38]/20 hover:border-[#0F1F38]"
            }`}
        >
            <Icon size={14} />
        </button>
    );
}

// ----------------------------------------------------------------------------
// StyleControls — the actual form (used inline OR inside a Popover).
// ----------------------------------------------------------------------------
export function StyleControls({ value, onChange, testidPrefix = "style" }) {
    const v = { ...EMPTY_STYLE, ...(value || {}) };
    const set = (patch) => onChange({ ...v, ...patch });

    // Custom-mode is remembered separately so that clicking "Custom…" reveals
    // an empty input immediately, without falling back to "Inherit" the way
    // it would if we derived the mode purely from the stored value.
    const initialSizeCustom =
        v.fontSize && !FONT_SIZES.includes(v.fontSize);
    const initialLhCustom =
        v.lineHeight && !LINE_HEIGHTS.includes(v.lineHeight);
    const [customSizeMode, setCustomSizeMode] = useState(initialSizeCustom);
    const [customLhMode, setCustomLhMode] = useState(initialLhCustom);
    const [customSize, setCustomSize] = useState(
        initialSizeCustom ? v.fontSize : "",
    );
    const [customLh, setCustomLh] = useState(
        initialLhCustom ? v.lineHeight : "",
    );

    // Derived select values.
    const sizeSelectValue = customSizeMode
        ? "__custom__"
        : v.fontSize && FONT_SIZES.includes(v.fontSize)
          ? v.fontSize
          : "__inherit__";
    const lhSelectValue = customLhMode
        ? "__custom__"
        : v.lineHeight && LINE_HEIGHTS.includes(v.lineHeight)
          ? v.lineHeight
          : "__inherit__";

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                    <label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                        Font Family
                    </label>
                    <Select
                        value={v.fontFamily || "__inherit__"}
                        onValueChange={(val) =>
                            set({ fontFamily: val === "__inherit__" ? "" : val })
                        }
                    >
                        <SelectTrigger
                            data-testid={`${testidPrefix}-font-family`}
                            className="mt-1 h-9 rounded-none border-[#0F1F38]/20 bg-white text-sm"
                        >
                            <SelectValue placeholder="Inherit" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__inherit__">
                                Inherit
                            </SelectItem>
                            {FONT_FAMILIES.map((f) => (
                                <SelectItem key={f.value} value={f.value}>
                                    {f.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                        Font Size
                    </label>
                    <Select
                        value={sizeSelectValue}
                        onValueChange={(val) => {
                            if (val === "__inherit__") {
                                setCustomSizeMode(false);
                                setCustomSize("");
                                set({ fontSize: "" });
                            } else if (val === "__custom__") {
                                // Reveal the custom input straight away; clear
                                // any preset so the input is the source of truth.
                                setCustomSizeMode(true);
                                set({ fontSize: customSize || "" });
                            } else {
                                setCustomSizeMode(false);
                                setCustomSize("");
                                set({ fontSize: val });
                            }
                        }}
                    >
                        <SelectTrigger
                            data-testid={`${testidPrefix}-font-size`}
                            className="mt-1 h-9 rounded-none border-[#0F1F38]/20 bg-white text-sm"
                        >
                            <SelectValue placeholder="Inherit" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__inherit__">
                                Inherit
                            </SelectItem>
                            {FONT_SIZES.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s}
                                </SelectItem>
                            ))}
                            <SelectItem value="__custom__">Custom…</SelectItem>
                        </SelectContent>
                    </Select>
                    {customSizeMode && (
                        <Input
                            autoFocus
                            value={customSize}
                            onChange={(e) => {
                                setCustomSize(e.target.value);
                                set({ fontSize: e.target.value });
                            }}
                            placeholder="e.g. 15px"
                            data-testid={`${testidPrefix}-font-size-custom`}
                            className="mt-1 h-8 rounded-none border-[#0F1F38]/20 bg-white text-xs"
                        />
                    )}
                </div>

                <div>
                    <label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                        Font Weight
                    </label>
                    <Select
                        value={v.fontWeight || "__inherit__"}
                        onValueChange={(val) =>
                            set({ fontWeight: val === "__inherit__" ? "" : val })
                        }
                    >
                        <SelectTrigger
                            data-testid={`${testidPrefix}-font-weight`}
                            className="mt-1 h-9 rounded-none border-[#0F1F38]/20 bg-white text-sm"
                        >
                            <SelectValue placeholder="Inherit" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__inherit__">
                                Inherit
                            </SelectItem>
                            {FONT_WEIGHTS.map((w) => (
                                <SelectItem key={w.value} value={w.value}>
                                    {w.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div>
                <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60 mb-1">
                    Style
                </div>
                <div className="flex flex-wrap gap-1">
                    <Toggle
                        active={!!v.bold}
                        onClick={() => set({ bold: !v.bold })}
                        icon={Bold}
                        label="Bold"
                        testid={`${testidPrefix}-bold`}
                    />
                    <Toggle
                        active={!!v.italic}
                        onClick={() => set({ italic: !v.italic })}
                        icon={Italic}
                        label="Italic"
                        testid={`${testidPrefix}-italic`}
                    />
                    <Toggle
                        active={!!v.underline}
                        onClick={() => set({ underline: !v.underline })}
                        icon={Underline}
                        label="Underline"
                        testid={`${testidPrefix}-underline`}
                    />
                    <div className="w-2" />
                    <Toggle
                        active={v.textAlign === "left"}
                        onClick={() =>
                            set({
                                textAlign:
                                    v.textAlign === "left" ? "" : "left",
                            })
                        }
                        icon={AlignLeft}
                        label="Align left"
                        testid={`${testidPrefix}-align-left`}
                    />
                    <Toggle
                        active={v.textAlign === "center"}
                        onClick={() =>
                            set({
                                textAlign:
                                    v.textAlign === "center" ? "" : "center",
                            })
                        }
                        icon={AlignCenter}
                        label="Align center"
                        testid={`${testidPrefix}-align-center`}
                    />
                    <Toggle
                        active={v.textAlign === "right"}
                        onClick={() =>
                            set({
                                textAlign:
                                    v.textAlign === "right" ? "" : "right",
                            })
                        }
                        icon={AlignRight}
                        label="Align right"
                        testid={`${testidPrefix}-align-right`}
                    />
                    <Toggle
                        active={v.textAlign === "justify"}
                        onClick={() =>
                            set({
                                textAlign:
                                    v.textAlign === "justify"
                                        ? ""
                                        : "justify",
                            })
                        }
                        icon={AlignJustify}
                        label="Justify"
                        testid={`${testidPrefix}-align-justify`}
                    />
                </div>
            </div>

            <div>
                <label className="text-[10px] tracking-label uppercase text-[#0F1F38]/60">
                    Line Height
                </label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                    <Select
                        value={lhSelectValue}
                        onValueChange={(val) => {
                            if (val === "__inherit__") {
                                setCustomLhMode(false);
                                setCustomLh("");
                                set({ lineHeight: "" });
                            } else if (val === "__custom__") {
                                setCustomLhMode(true);
                                set({ lineHeight: customLh || "" });
                            } else {
                                setCustomLhMode(false);
                                setCustomLh("");
                                set({ lineHeight: val });
                            }
                        }}
                    >
                        <SelectTrigger
                            data-testid={`${testidPrefix}-line-height`}
                            className="h-9 rounded-none border-[#0F1F38]/20 bg-white text-sm"
                        >
                            <SelectValue placeholder="Inherit" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__inherit__">
                                Inherit
                            </SelectItem>
                            {LINE_HEIGHTS.map((l) => (
                                <SelectItem key={l} value={l}>
                                    {l}
                                </SelectItem>
                            ))}
                            <SelectItem value="__custom__">Custom…</SelectItem>
                        </SelectContent>
                    </Select>
                    {customLhMode ? (
                        <Input
                            autoFocus
                            value={customLh}
                            onChange={(e) => {
                                setCustomLh(e.target.value);
                                set({ lineHeight: e.target.value });
                            }}
                            placeholder="e.g. 1.35"
                            data-testid={`${testidPrefix}-line-height-custom`}
                            className="h-9 rounded-none border-[#0F1F38]/20 bg-white text-xs"
                        />
                    ) : (
                        <div />
                    )}
                </div>
            </div>

            <div className="flex items-center justify-between pt-1">
                <button
                    type="button"
                    onClick={() => {
                        setCustomSize("");
                        setCustomLh("");
                        setCustomSizeMode(false);
                        setCustomLhMode(false);
                        onChange({ ...EMPTY_STYLE });
                    }}
                    data-testid={`${testidPrefix}-reset`}
                    className="inline-flex items-center gap-1 text-[10px] tracking-label uppercase text-[#0F1F38]/70 hover:text-[#0F1F38] transition-colors"
                >
                    <RotateCcw size={11} /> Reset to Default
                </button>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------------
// FormatButton — the trigger + popover wrapper that admins actually click.
// If `label` is passed, the popover shows a heading.
// ----------------------------------------------------------------------------
export default function FormatButton({
    value,
    onChange,
    testid,
    label = "Formatting",
    size = "sm",
}) {
    const sizeClass = size === "sm" ? "h-8 px-2 text-[10px]" : "h-9 px-3 text-xs";
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid={testid}
                    aria-label={label}
                    title={label}
                    className={`inline-flex items-center gap-1 ${sizeClass} tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors bg-white`}
                >
                    <Type size={12} /> Format
                </button>
            </PopoverTrigger>
            <PopoverContent
                data-testid={`${testid}-popover`}
                className="w-80 bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none p-4"
                align="end"
            >
                <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60 mb-3">
                    {label}
                </div>
                <StyleControls
                    value={value}
                    onChange={onChange}
                    testidPrefix={testid}
                />
            </PopoverContent>
        </Popover>
    );
}
